import React from 'react'
import { GetServerSideProps, NextPage } from 'next/types'
import { PageProps } from '@/core/types'

const EN_TITLE = 'Country Not Supported'
const EN_MESSAGE =
  'This domain is not mapped to a valid supported country in this environment.'

const ES_TITLE = 'País no soportado'
const ES_MESSAGE =
  'Este dominio no está mapeado a un país válido y soportado en este entorno.'

const PT_TITLE = 'País não suportado'
const PT_MESSAGE =
  'Este domínio não está mapeado para um país válido e suportado neste ambiente.'

const titleByLanguage: Record<string, string> = {
  de: 'Land nicht unterstützt',
  en: EN_TITLE,
  es: ES_TITLE,
  fil: 'Hindi suportado ang bansa',
  fr: 'Pays non pris en charge',
  id: 'Negara tidak didukung',
  ko: '지원되지 않는 국가',
  ms: 'Negara tidak disokong',
  nl: 'Land niet ondersteund',
  pt: PT_TITLE,
  sw: 'Nchi haitumiki',
  th: 'ไม่รองรับประเทศ',
  vi: 'Quốc gia không được hỗ trợ',
  zh: '不支持该国家'
}

const messageByLanguage: Record<string, string> = {
  de: 'Diese Domain ist in dieser Umgebung keinem gültigen unterstützten Land zugeordnet.',
  en: EN_MESSAGE,
  es: ES_MESSAGE,
  fil: 'Ang domain na ito ay hindi naka-map sa isang valid at suportadong bansa sa environment na ito.',
  fr: "Ce domaine n'est pas associé à un pays valide pris en charge dans cet environnement.",
  id: 'Domain ini tidak dipetakan ke negara yang valid dan didukung di lingkungan ini.',
  ko: '이 도메인은 이 환경에서 유효하고 지원되는 국가와 연결되어 있지 않습니다.',
  ms: 'Domain ini tidak dipetakan kepada negara yang sah dan disokong dalam persekitaran ini.',
  nl: 'Dit domein is in deze omgeving niet gekoppeld aan een geldig ondersteund land.',
  pt: PT_MESSAGE,
  sw: 'Domeni hii haijahusishwa na nchi halali inayotumika katika mazingira haya.',
  th: 'โดเมนนี้ไม่ได้ถูกแมปกับประเทศที่ถูกต้องและรองรับในสภาพแวดล้อมนี้',
  vi: 'Tên miền này không được ánh xạ tới một quốc gia hợp lệ và được hỗ trợ trong môi trường này.',
  zh: '该域名在当前环境中未映射到有效且受支持的国家。'
}

interface CountryNotSupportedProps extends Partial<PageProps> {
  errorTitle: string
  errorMessage: string
}

const getLanguageFromHeader = (
  acceptLanguageHeader: string | undefined
): string => {
  if (!acceptLanguageHeader) return 'en'

  const firstLanguage = acceptLanguageHeader.split(',')[0]?.trim().toLowerCase()
  if (!firstLanguage) return 'en'

  if (firstLanguage.startsWith('pt')) return 'pt'
  if (firstLanguage.startsWith('es')) return 'es'
  if (firstLanguage.startsWith('fr')) return 'fr'
  if (firstLanguage.startsWith('de')) return 'de'
  if (firstLanguage.startsWith('nl')) return 'nl'
  if (firstLanguage.startsWith('sw')) return 'sw'
  if (firstLanguage.startsWith('th')) return 'th'
  if (firstLanguage.startsWith('vi')) return 'vi'
  if (firstLanguage.startsWith('ko')) return 'ko'
  if (firstLanguage.startsWith('id')) return 'id'
  if (firstLanguage.startsWith('ms')) return 'ms'
  if (firstLanguage.startsWith('fil') || firstLanguage.startsWith('tl'))
    return 'fil'
  if (firstLanguage.startsWith('zh')) return 'zh'

  return 'en'
}

const CountryNotSupportedPage: NextPage<CountryNotSupportedProps> = ({
  errorTitle,
  errorMessage
}) => {
  return (
    <div className="page-container">
      <div
        style={{
          height: '80px',
          background: '#ffff00',
          marginBottom: 20
        }}
      ></div>
      <div className="bees-grid is-margin-top-m is-margin-bottom-m">
        <div className="bees-col is-4-col-phone is-8-col-tablet is-12-col-desktop">
          <h1 className="bees-heading is-text-align-left is-margin-bottom-s">
            {errorTitle}
          </h1>
          <p className="bees-paragraph is-text-align-left">{errorMessage}</p>
        </div>
      </div>
    </div>
  )
}

export const getServerSideProps: GetServerSideProps<
  CountryNotSupportedProps
> = async (context) => {
  const language = getLanguageFromHeader(
    context.req.headers['accept-language'] as string | undefined
  )

  return {
    props: {
      pathname: '/country_not_supported',
      errorTitle: titleByLanguage[language] || EN_TITLE,
      errorMessage: messageByLanguage[language] || EN_MESSAGE,
      pageKey: 'COUNTRY_NOT_SUPPORTED'
    }
  }
}

export default CountryNotSupportedPage
